/**
 * The vehicles the player has had: the one being driven, and the ones left
 * where they stopped.
 *
 * A parked car is an instance among a tile's props (see `build/parking.ts`
 * and `flora.ts`). Taking one swaps it for a full model, with wheels that
 * turn, a door that opens and glass you can see the driver through. That
 * model stays in the world after the player gets out, as solid as it was
 * parked, so it can be walked round and got back into. Positions are kept in
 * latitude and longitude, like the player's, so an origin rebase does not
 * move them. The last few are kept; older ones are let go.
 */

import type { Object3D, Scene, SpotLight } from 'three';
import type { Vector3 } from 'three';
import type { Species } from '../core/biome/flora.js';
import type { VehicleSpecies } from '../core/build/parking.js';
import type { Way } from '../core/traffic/findway.js';
import { VEHICLE_SIZE } from '../core/build/parking.js';
import type { MotoModel } from './Motorbike.js';
import { createMotorbike } from './Motorbike.js';
import type { ScooterModel } from './Scooter.js';
import { createScooter } from './Scooter.js';
import type { CarKind, VehicleModel } from './Vehicle.js';
import { createVehicle, isBus, orientToSlope } from './Vehicle.js';
import type { Tank } from '../core/economy/energy.js';
import type { VehicleDamage } from '../core/vehicle/damage.js';
import type { Wreck } from './crumple.js';
import type { Fall, Sliding } from '../core/vehicle/collision.js';
import type { PoliceKit } from './police.js';
import { makePolice } from './police.js';
import { FALLEN } from '../core/vehicle/collision.js';
import type { Handling } from '../core/vehicle/handling.js';
import { foundTank } from '../core/economy/energy.js';

/** A two-wheeler's model: the motocrosser or the e-scooter; `kind` says which. */
export type TwoWheelerModel = MotoModel | ScooterModel;

export interface FleetVehicle {
  /** A car (trucks and buses too), or a two-wheeler ridden in the motorbike's mode. */
  type: 'car' | 'moto';
  car?: VehicleModel;
  bike?: TwoWheelerModel;
  species: VehicleSpecies;
  paint: number;
  lat: number;
  lon: number;
  /** Height of the ground it stands on, and its yaw. */
  y: number;
  yaw: number;
  /** Nose-up and right-side-up tilt, radians: how it sits on a slope. */
  pitch: number;
  roll: number;
  /**
   * Where its headlight beam mounts. Each model's own beam is taken out: a
   * light added to the scene recompiles every material's shader, so there
   * is one beam per machine type, moved into whichever is being driven. See
   * `mountBeam`.
   */
  beamMount: Object3D;
  /** Where each of its beams sat, and where each aimed: two for a car, one for a bike. */
  beamAt: Vector3[];
  beamTarget: Vector3[];
  /** What it runs on, and how much is left. See `core/economy/energy.ts`. */
  tank: Tank;
  /** What crashes have done to it, and its model's wreck: see `three/crumple.ts`. */
  damage?: VehicleDamage;
  wreck?: Wreck;
  /** How it drives now, worked out again after each crush. */
  handling?: Handling;
  /** Sliding after a hit, until its tyres stop it: scene velocity and spin, and how hard it scrubs, m/s². */
  motion?: Sliding & { drag: number };
  /** Taken out of the traffic by a crash: seconds it has stood since. Undefined for any other. */
  fromTraffic?: number;
  /** A motorbike knocked over, falling to its right (1) or left (−1), or lying there. */
  fall?: Fall & { side: 1 | -1 };
  /** A police car: its light bar, and whether its lights and siren are on. */
  police?: { kit: PoliceKit; siren: boolean };
  /** A police car off the road after the player (see `core/traffic/pursuit.ts`): how fast it goes, and which way (scene x, z, m/s). */
  chaser?: { speed: number; vx: number; vz: number; stuck: number; way?: Way; replan: number };
  /** An articulated truck's trailer axles, where they are: the trailer is pulled along behind from there. */
  trailerAxle?: { lat: number; lon: number };
}

const KIND: Partial<Record<Species, CarKind>> = {
  carHatch: 'hatch', carSedan: 'sedan', carEstate: 'estate', carSuv: 'suv', carVan: 'van', carPickup: 'pickup',
  truckBox: 'truck', truckSemi: 'semi', busArea: 'busArea', busExpress: 'busExpress', busMetro: 'busMetro', busCity: 'busCity',
};

/**
 * The tank of a vehicle parked at (lat, lon): the same car is the same tank
 * each time it is found — and before it is got into, to say what it runs on.
 */
export function tankOf(species: VehicleSpecies, paint: number, lat: number, lon: number): Tank {
  const seed = Math.round(lat * 1e5) * 31 + Math.round(lon * 1e5) + paint;
  const kind = KIND[species];
  return foundTank(kind === undefined ? (species === 'parkedScooter' ? 'scooter' : 'moto') : isBus(kind) ? 'bus' : kind, seed);
}

/** Left-behind vehicles to keep before letting the oldest go. */
const KEEP = 10;

export class Fleet {
  readonly vehicles: FleetVehicle[] = [];
  /** The one being driven, if any. It is placed by the driving, not here. */
  driven: FleetVehicle | undefined;

  constructor(private readonly scene: Scene) {}

  /** A new vehicle of a parked species, standing at (lat, lon, y), facing `yaw`. */
  add(species: VehicleSpecies, paint: number, lat: number, lon: number, y: number, yaw: number, police = false): FleetVehicle {
    const kind = KIND[species];
    const tank = tankOf(species, paint, lat, lon);
    const electric = tank.energy === 'electric';
    const mount = { beamMount: this.scene as Object3D, beamAt: [] as Vector3[], beamTarget: [] as Vector3[], pitch: 0, roll: 0, tank };
    const vehicle: FleetVehicle = kind === undefined
      ? { type: 'moto', bike: species === 'parkedScooter' ? createScooter() : createMotorbike(), species, paint, lat, lon, y, yaw, ...mount }
      : { type: 'car', car: createVehicle(kind, paint, electric), species, paint, lat, lon, y, yaw, ...mount };
    const beams: SpotLight[] = vehicle.car?.headlights ?? [vehicle.bike!.headlight];
    vehicle.beamMount = beams[0].parent!;
    for (const beam of beams) {
      vehicle.beamAt.push(beam.position.clone());
      vehicle.beamTarget.push(beam.target.position.clone());
      beam.parent!.remove(beam, beam.target);
    }
    if (police && vehicle.car !== undefined) vehicle.police = { kit: makePolice(vehicle.car), siren: false };
    const root = vehicle.car?.root ?? vehicle.bike!.root;
    root.matrixAutoUpdate = true;
    this.scene.add(root);
    this.vehicles.push(vehicle);
    this.trim();
    return vehicle;
  }

  /** Move the shared headlight beams into `v`, where its own were, and make them `v`'s. */
  mountBeams(v: FleetVehicle, beams: SpotLight[]): void {
    beams.forEach((beam, i) => {
      v.beamMount.add(beam, beam.target);
      beam.position.copy(v.beamAt[i]);
      beam.target.position.copy(v.beamTarget[i]);
    });
    if (v.car !== undefined) v.car.headlights = [beams[0], beams[1]];
    else v.bike!.headlight = beams[0];
  }

  root(v: FleetVehicle) {
    return v.car?.root ?? v.bike!.root;
  }

  /** Stand the left-behind vehicles where they were left. `toLocal` gives scene x, z. */
  place(toLocal: (lat: number, lon: number) => { east: number; north: number }): void {
    for (const v of this.vehicles) {
      if (v === this.driven) continue;
      const at = toLocal(v.lat, v.lon);
      const root = this.root(v);
      root.visible = true;
      root.position.set(at.east, v.y, -at.north);
      // Sitting on the slope where it was left; a bike leans on its stand,
      // or lies on its side, knocked over, propped a little by its bars and
      // pegs and lifted by them. Leaning right is a negative turn about z.
      orientToSlope(root, v.yaw, Math.tan(v.pitch), Math.tan(v.roll));
      if (v.type === 'moto') {
        const f = v.fall, over = f === undefined ? 0 : f.angle / FALLEN;
        root.rotateZ(0.14 + (-(f?.side ?? 1) * FALLEN - 0.14) * over);
        root.position.y += 0.15 * over;
      }
    }
  }

  /** The nearest left-behind vehicle within `radius` of scene (x, z). */
  nearest(x: number, z: number, radius: number, toLocal: (lat: number, lon: number) => { east: number; north: number },
    accept?: (cx: number, cz: number, v: FleetVehicle) => boolean): FleetVehicle | undefined {
    let best: FleetVehicle | undefined;
    let bestD = radius;
    for (const v of this.vehicles) {
      if (v === this.driven) continue;
      const at = toLocal(v.lat, v.lon);
      const d = Math.hypot(at.east - x, -at.north - z);
      if (d < bestD && (accept === undefined || accept(at.east, -at.north, v))) { bestD = d; best = v; }
    }
    return best;
  }

  /**
   * Push a round body at scene (x, z), spanning `feet`..`head`, out of any
   * left-behind vehicle it overlaps but `skip`, and say which. Undefined when clear.
   */
  collide(x: number, z: number, radius: number, feet: number, head: number,
    toLocal: (lat: number, lon: number) => { east: number; north: number }, skip?: FleetVehicle): { x: number; z: number; vehicle: FleetVehicle } | undefined {
    let px = x, pz = z, hit: FleetVehicle | undefined;
    for (const v of this.vehicles) {
      if (v === this.driven || v === skip) continue;
      const size = VEHICLE_SIZE[v.species];
      if (head <= v.y || feet >= v.y + size.height) continue;
      const at = toLocal(v.lat, v.lon);
      const cx = at.east, cz = -at.north;
      // Into the vehicle's frame: u across, w along.
      const c = Math.cos(v.yaw), s = Math.sin(v.yaw);
      const dx = px - cx, dz = pz - cz;
      const u = dx * c - dz * s, w = dx * s + dz * c;
      const hu = size.halfWidth + radius, hw = size.halfLength + radius;
      if (Math.abs(u) >= hu || Math.abs(w) >= hw) continue;
      // Out through the nearer side.
      let nu = u, nw = w;
      if (hu - Math.abs(u) < hw - Math.abs(w)) nu = Math.sign(u || 1) * hu;
      else nw = Math.sign(w || 1) * hw;
      px = cx + nu * c + nw * s;
      pz = cz - nu * s + nw * c;
      hit = v;
    }
    // Left-behind trucks' trailers, as they were left swung.
    for (const v of this.vehicles) {
      const t = v.car?.trailer;
      if (v === this.driven || t === undefined || head <= v.y || feet >= v.y + 4) continue;
      const at = toLocal(v.lat, v.lon);
      const yaw = v.yaw + t.root.rotation.y;
      const bx = Math.sin(v.yaw), bz = Math.cos(v.yaw);
      const tb = { x: Math.sin(yaw), z: Math.cos(yaw) };
      const mid = (t.length - 1.2) / 2;
      const cx = at.east + bx * t.kingpinZ + tb.x * mid, cz = -at.north + bz * t.kingpinZ + tb.z * mid;
      const c = Math.cos(yaw), s = Math.sin(yaw);
      const dx = px - cx, dz = pz - cz;
      const u = dx * c - dz * s, w = dx * s + dz * c;
      const hu = t.width / 2 + radius, hw = (t.length + 1.2) / 2 + radius;
      if (Math.abs(u) >= hu || Math.abs(w) >= hw) continue;
      let nu = u, nw = w;
      if (hu - Math.abs(u) < hw - Math.abs(w)) nu = Math.sign(u || 1) * hu;
      else nw = Math.sign(w || 1) * hw;
      px = cx + nu * c + nw * s;
      pz = cz - nu * s + nw * c;
      hit = v;
    }
    return hit === undefined ? undefined : { x: px, z: pz, vehicle: hit };
  }

  /** Let go of one vehicle: it leaves the world. */
  remove(v: FleetVehicle): void {
    const i = this.vehicles.indexOf(v);
    if (i < 0 || v === this.driven) return;
    this.vehicles.splice(i, 1);
    this.scene.remove(this.root(v));
  }

  /** Let go of the oldest left-behind vehicles past `KEEP`. */
  private trim(): void {
    while (this.vehicles.filter(v => v !== this.driven).length > KEEP) {
      const i = this.vehicles.findIndex(v => v !== this.driven);
      const [gone] = this.vehicles.splice(i, 1);
      this.scene.remove(this.root(gone));
    }
  }
}
